import * as React from "react";
import { Component, CSSProperties } from "react";
import { Alert, AlertTitle, CircularProgress, Snackbar, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, IconButton, Box } from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

interface InstallationState {
    isInstalling: boolean;
    installDeferred: boolean;
    lastAttempt: string | null;
    installationId: string | null;
    serviceInstalled: boolean;
    installedVersion: string | null;
}

interface ServiceInstallResult {
    success: boolean;
    skipped: boolean;
    reason?: string;
    error?: string;
    installedVersion?: string;
    previousVersion?: string;
}

interface ServiceInstallNotificationProps {
    isVisible: boolean;
    installState: "checking" | "prompt" | "installing" | "success" | "deferred" | "error" | "idle";
    errorMessage?: string;
    installedVersion?: string;
    onAccept?: () => void;
    onDismiss?: () => void;
}

interface ServiceInstallNotificationState {
    isOpen: boolean;
    isExpanded: boolean;
}

const alertStyle: CSSProperties = {
    minWidth: "350px"
};

const collapsedStyle: CSSProperties = {
    minWidth: "auto",
    cursor: "pointer"
};

export default class ServiceInstallNotification extends Component<ServiceInstallNotificationProps, ServiceInstallNotificationState> {
    constructor(props: ServiceInstallNotificationProps) {
        super(props);
        this.state = {
            isOpen: props.isVisible,
            isExpanded: true
        };
    }

    componentDidUpdate(prevProps: ServiceInstallNotificationProps) {
        if (prevProps.isVisible !== this.props.isVisible) {
            this.setState({ isOpen: this.props.isVisible, isExpanded: true });
        }
    }

    private toggleExpanded = () => {
        this.setState(prev => ({ isExpanded: !prev.isExpanded }));
    };

    render() {
        const { installState, errorMessage, installedVersion, onAccept, onDismiss } = this.props;
        const { isOpen, isExpanded } = this.state;

        if (installState === "idle") {
            return null;
        }

        // Show dialog for prompt state (accept-only)
        if (installState === "prompt") {
            return (
                <Dialog open={isOpen} onClose={() => {}}>
                    <DialogTitle>Install iDART Background Service</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Your account has HDC V3 devices that require the iDART Background Service
                            for automatic data collection. The service will be installed now.
                        </DialogContentText>
                        <DialogContentText sx={{ marginTop: 2 }}>
                            You will be prompted to approve administrator access.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onAccept} variant="contained" color="primary" autoFocus>
                            Install Now
                        </Button>
                    </DialogActions>
                </Dialog>
            );
        }

        const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
            // Only close on explicit actions, not on clickaway or focus loss
            if (reason === "clickaway") {
                return;
            }
            this.setState({ isOpen: false });
            if (onDismiss) {
                onDismiss();
            }
        };

        const handleExplicitClose = () => {
            this.setState({ isOpen: false });
            if (onDismiss) {
                onDismiss();
            }
        };

        return (
            <Snackbar
                open={isOpen}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                autoHideDuration={installState === "success" ? 6000 : null}
                onClose={handleClose}
                sx={{ marginBottom: "60px" }}
            >
                <Box>
                    {isExpanded ? (
                        this.renderAlert(installState, errorMessage, installedVersion, onAccept, handleExplicitClose)
                    ) : (
                        this.renderCollapsedAlert(installState)
                    )}
                </Box>
            </Snackbar>
        );
    }

    private renderCollapsedAlert(installState: string): React.ReactElement {
        const severity = this.getSeverity(installState);
        const title = this.getShortTitle(installState);

        return (
            <Alert
                severity={severity}
                style={collapsedStyle}
                icon={installState === "checking" || installState === "installing" ? <CircularProgress size={16} /> : undefined}
                action={
                    <IconButton size="small" onClick={this.toggleExpanded} color="inherit">
                        <ExpandMoreIcon />
                    </IconButton>
                }
            >
                {title}
            </Alert>
        );
    }

    private getSeverity(installState: string): "info" | "success" | "warning" | "error" {
        switch (installState) {
            case "checking":
            case "installing":
                return "info";
            case "success":
                return "success";
            case "deferred":
                return "warning";
            case "error":
            default:
                return "error";
        }
    }

    private getShortTitle(installState: string): string {
        switch (installState) {
            case "checking":
                return "Checking...";
            case "installing":
                return "Installing...";
            case "success":
                return "Installed";
            case "deferred":
                return "Deferred";
            case "error":
            default:
                return "Failed";
        }
    }

    private renderAlert(
        installState: string,
        errorMessage?: string,
        installedVersion?: string,
        onRetry?: () => void,
        onClose?: () => void
    ): React.ReactElement {
        const collapseButton = (
            <IconButton size="small" onClick={this.toggleExpanded} color="inherit" sx={{ ml: 1 }}>
                <ExpandLessIcon />
            </IconButton>
        );

        switch (installState) {
            case "checking":
                return (
                    <Alert severity="info" style={alertStyle} icon={<CircularProgress size={20} />} action={collapseButton}>
                        <AlertTitle>Checking Requirements</AlertTitle>
                        Checking if your account requires the iDART Background Service...
                    </Alert>
                );

            case "installing":
                return (
                    <Alert severity="info" style={alertStyle} icon={<CircularProgress size={20} />} action={collapseButton}>
                        <AlertTitle>Installing Background Service</AlertTitle>
                        Please approve the installation when prompted. This may take 10+ minutes if SQL Express needs to be installed.
                    </Alert>
                );

            case "success":
                return (
                    <Alert severity="success" style={alertStyle} onClose={onClose} action={collapseButton}>
                        <AlertTitle>Installation Complete</AlertTitle>
                        The iDART Background Service {installedVersion ? `(v${installedVersion})` : ""} has been installed successfully.
                    </Alert>
                );

            case "deferred":
                return (
                    <Alert
                        severity="warning"
                        style={alertStyle}
                        onClose={onClose}
                        action={
                            <>
                                {onRetry ? (
                                    <Button color="inherit" size="small" onClick={onRetry}>
                                        Install Now
                                    </Button>
                                ) : null}
                                {collapseButton}
                            </>
                        }
                    >
                        <AlertTitle>Installation Required</AlertTitle>
                        The Background Service installation was cancelled. It will be offered again on your next login.
                    </Alert>
                );

            case "error":
            default:
                return (
                    <Alert
                        severity="error"
                        style={alertStyle}
                        onClose={onClose}
                        action={collapseButton}
                    >
                        <AlertTitle>Installation Failed</AlertTitle>
                        {errorMessage || "Failed to install the Background Service. It will be offered again on your next login."}
                    </Alert>
                );
        }
    }
}

export type { InstallationState, ServiceInstallResult, ServiceInstallNotificationProps };
